// Overnight Shift Utilities
// Handles grouping and calculation of time records for employees working overnight shifts
import { formatLocalYMD, getLocalHours, addDaysLocalYMD, CHICAGO_TIMEZONE } from './timezone';

// ============================================================================
// PERFORMANCE: Work Day Cache
// Memoizes getRecordWorkDay results to avoid O(n²) recalculations
// Cache invalidates when records array changes (based on length + first/last IDs)
// ============================================================================
const workDayCache = new Map();
let lastRecordsHash = null;

const getRecordsHash = (records) => {
  if (!records || records.length === 0) return 'empty';
  const first = records[0]?.id || 'none';
  const last = records[records.length - 1]?.id || 'none';
  return `${records.length}-${first}-${last}`;
};

const invalidateCacheIfNeeded = (records) => {
  const currentHash = getRecordsHash(records);
  if (currentHash !== lastRecordsHash) {
    workDayCache.clear();
    lastRecordsHash = currentHash;
  }
};

// Export for testing purposes
export const clearWorkDayCache = () => {
  workDayCache.clear();
  lastRecordsHash = null;
};

/**
 * Groups time records by work shift rather than calendar day
 * This properly handles overnight shifts where employees work across two calendar days
 *
 * @param {Array} records - Array of time records
 * @param {string} employeeId - Employee ID to filter records
 * @returns {Object} Records grouped by shift date
 */
export const groupRecordsByShift = (records, employeeId) => {
  const employeeRecords = records
    .filter(r => r.employeeId === employeeId && !r.crossedOut)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const shiftGroups = {};
  let currentShiftDate = null;
  let lastClockIn = null;

  employeeRecords.forEach(record => {
    const recordDate = new Date(record.timestamp);
    const recordDateStr = formatLocalYMD(record.timestamp);
    const recordHour = getLocalHours(record.timestamp);

    if (record.action === 'clock-in') {
      // Start of a new shift
      // Handle overnight shifts:
      // - If clocking in after 10 PM (22:00-23:59), assign to next day
      // - If clocking in in early morning hours (00:00-05:59), it's a night shift for current day
      if (recordHour >= 22) {
        // Late night clock-in (10 PM or later) - assign to next local day
        currentShiftDate = addDaysLocalYMD(recordDate, 1);
      } else if (recordHour >= 0 && recordHour < 6) {
        // Early morning clock-in (midnight to 6 AM) - night shift for current day
        currentShiftDate = recordDateStr;
      } else {
        // Regular daytime shift - assign to current day
        currentShiftDate = recordDateStr;
      }
      lastClockIn = recordDate;
    } else if (record.action === 'clock-out') {
      // End of shift
      if (currentShiftDate && lastClockIn) {
        // If clock-out is more than 12 hours after clock-in, it's likely an overnight shift
        const hoursSinceClockIn = (recordDate - lastClockIn) / (1000 * 60 * 60);

        if (hoursSinceClockIn > 12 || (getLocalHours(lastClockIn) >= 22 && recordHour <= 12)) {
          // This is an overnight shift - keep the shift date as the "work day"
          // Don't change currentShiftDate
        } else if (!currentShiftDate) {
          // No previous clock-in found, use current date
          currentShiftDate = recordDateStr;
        }
      } else if (!currentShiftDate) {
        // No clock-in found, use current date
        currentShiftDate = recordDateStr;
      }
    } else {
      // lunch-in, lunch-out - continue with current shift date
      if (!currentShiftDate) {
        // If no shift date set yet, use current date
        currentShiftDate = recordDateStr;
      }
    }

    // Add record to the appropriate shift group
    if (currentShiftDate) {
      if (!shiftGroups[currentShiftDate]) {
        shiftGroups[currentShiftDate] = [];
      }
      shiftGroups[currentShiftDate].push(record);
    }

    // Reset shift date on clock-out for next shift
    if (record.action === 'clock-out') {
      currentShiftDate = null;
      lastClockIn = null;
    }
  });

  return shiftGroups;
};

/**
 * Determines the work day for a specific time record
 * Handles overnight shifts by assigning work to the day the shift "belongs to"
 *
 * @param {Object} record - Time record
 * @param {Array} allRecords - All records for context
 * @returns {string} Work day in YYYY-MM-DD format
 */
export const getRecordWorkDay = (record, allRecords, companyId = null) => {
  // PERFORMANCE: Check cache first
  invalidateCacheIfNeeded(allRecords);
  const cacheKey = `${record.id}-${companyId || 'default'}`;
  if (workDayCache.has(cacheKey)) {
    return workDayCache.get(cacheKey);
  }

  let result;

  // COMPANY-SPECIFIC LOGIC for company-1757441106691
  if (companyId === 'company-1757441106691') {
    result = getRecordWorkDay_Company1757441106691(record, allRecords);
  } else {
    // DEFAULT LOGIC: Original overnight shift grouping for all other companies
    result = getRecordWorkDay_Default(record, allRecords);
  }

  // Cache the result
  workDayCache.set(cacheKey, result);
  return result;
};

/**
 * Company-specific logic for company-1757441106691
 * Groups complete shifts together - overnight shifts properly grouped with the clock-in day
 */
const getRecordWorkDay_Company1757441106691 = (record, allRecords) => {
  // Use originalTimestamp if available (for edited records)
  const timestampToUse = record.originalTimestamp || record.timestamp;
  const recordDateStr = formatLocalYMD(timestampToUse);
  const recordHour = getLocalHours(timestampToUse);

  // For clock-in: check if it's early morning (12 AM - 6 AM)
  if (record.action === 'clock-in') {
    // If clocking in between midnight and 6 AM, move to previous day
    if (recordHour >= 0 && recordHour < 6) {
      return addDaysLocalYMD(timestampToUse, -1);
    }
    return recordDateStr;
  }

  // For clock-out, lunch-out, lunch-in: ALWAYS find the shift's clock-in and group with that day
  if (record.action === 'clock-out' || record.action === 'lunch-out' || record.action === 'lunch-in') {
    const employeeRecords = allRecords
      .filter(r => r.employeeId === record.employeeId && !r.crossedOut)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    const recordIndex = employeeRecords.findIndex(r => r.id === record.id);

    // Look backwards for the clock-in that started this shift
    for (let i = recordIndex - 1; i >= 0; i--) {
      const previousRecord = employeeRecords[i];
      if (previousRecord.action === 'clock-in') {
        // Use originalTimestamp for clock-in date if available
        const clockInTime = previousRecord.originalTimestamp || previousRecord.timestamp;
        const clockInHour = getLocalHours(clockInTime);
        const clockInDateStr = formatLocalYMD(clockInTime);

        // If the clock-in was early morning (12 AM - 6 AM), it belongs to previous day
        if (clockInHour >= 0 && clockInHour < 6) {
          return addDaysLocalYMD(clockInTime, -1);
        }

        // Otherwise, group this action with the clock-in's day
        return clockInDateStr;
      }
      // If we hit another clock-out, stop looking (new shift boundary)
      if (previousRecord.action === 'clock-out') {
        break;
      }
    }

    // No clock-in found - check if this morning action belongs to previous day
    // Extended to 12 PM to catch overnight shifts that end later in the morning (e.g., 8:30 AM)
    if (recordHour >= 0 && recordHour < 12) {
      return addDaysLocalYMD(timestampToUse, -1);
    }
  }

  // For other actions: use actual date
  return recordDateStr;
};

/**
 * Default logic for all other companies
 * Groups complete shifts together - overnight shifts properly grouped with the clock-in day
 */
const getRecordWorkDay_Default = (record, allRecords) => {
  // Use originalTimestamp if available (for edited records), otherwise use timestamp
  const timestampToUse = record.originalTimestamp || record.timestamp;
  const recordDateStr = formatLocalYMD(timestampToUse);
  const recordHour = getLocalHours(timestampToUse);

  // For clock-in: check if it's early morning (12 AM - 6 AM)
  if (record.action === 'clock-in') {
    // If clocking in between midnight and 6 AM, move to previous day
    if (recordHour >= 0 && recordHour < 6) {
      return addDaysLocalYMD(timestampToUse, -1);
    }
    return recordDateStr;
  }

  // For clock-out, lunch-out, lunch-in: ALWAYS find the shift's clock-in and group with that day
  if (record.action === 'clock-out' || record.action === 'lunch-out' || record.action === 'lunch-in') {
    const employeeRecords = allRecords
      .filter(r => r.employeeId === record.employeeId && !r.crossedOut)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    const recordIndex = employeeRecords.findIndex(r => r.id === record.id);

    // Look backwards for the clock-in that started this shift
    for (let i = recordIndex - 1; i >= 0; i--) {
      const previousRecord = employeeRecords[i];
      if (previousRecord.action === 'clock-in') {
        // Use originalTimestamp for clock-in date if available
        const clockInTime = previousRecord.originalTimestamp || previousRecord.timestamp;
        const clockInHour = getLocalHours(clockInTime);
        const clockInDateStr = formatLocalYMD(clockInTime);

        // If the clock-in was early morning (12 AM - 6 AM), it belongs to previous day
        if (clockInHour >= 0 && clockInHour < 6) {
          return addDaysLocalYMD(clockInTime, -1);
        }

        // Otherwise, group this action with the clock-in's day
        return clockInDateStr;
      }
      // If we hit another clock-out, stop looking (new shift boundary)
      if (previousRecord.action === 'clock-out') {
        break;
      }
    }

    // No clock-in found - check if this morning action belongs to previous day
    // Extended to 12 PM to catch overnight shifts that end later in the morning (e.g., 8:30 AM)
    if (recordHour >= 0 && recordHour < 12) {
      return addDaysLocalYMD(timestampToUse, -1);
    }
  }

  // For other actions: use actual date
  return recordDateStr;
};

/**
 * Groups all time records by work day, handling overnight shifts properly
 *
 * @param {Array} records - All time records
 * @param {string} companyId - Company ID for company-specific logic
 * @returns {Object} Records grouped by work day
 */
export const groupAllRecordsByWorkDay = (records, companyId = null) => {
  const workDayGroups = {};

  records.forEach(record => {
    const workDay = getRecordWorkDay(record, records, companyId);

    if (!workDayGroups[workDay]) {
      workDayGroups[workDay] = [];
    }

    workDayGroups[workDay].push(record);
  });

  return workDayGroups;
};

/**
 * Calculates if a shift is an overnight shift
 *
 * @param {Array} shiftRecords - Records for a single shift
 * @returns {boolean} True if this is an overnight shift
 */
export const isOvernightShift = (shiftRecords) => {
  if (shiftRecords.length === 0) return false;

  const sortedRecords = shiftRecords.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const firstRecord = sortedRecords[0];
  const lastRecord = sortedRecords[sortedRecords.length - 1];

  // Check if records span across different local calendar days
  const firstDayYMD = formatLocalYMD(firstRecord.timestamp);
  const lastDayYMD = formatLocalYMD(lastRecord.timestamp);

  return firstDayYMD !== lastDayYMD;
};

/**
 * Formats shift information for display
 *
 * @param {Array} shiftRecords - Records for a single shift
 * @returns {Object} Formatted shift information
 */
export const formatShiftInfo = (shiftRecords) => {
  if (shiftRecords.length === 0) {
    return { isOvernight: false, displayDate: '', timeSpan: '' };
  }

  const sortedRecords = shiftRecords.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const firstRecord = sortedRecords[0];
  const lastRecord = sortedRecords[sortedRecords.length - 1];

  const firstDate = new Date(firstRecord.timestamp);
  const lastDate = new Date(lastRecord.timestamp);
  const isOvernight = isOvernightShift(shiftRecords);

  let displayDate, timeSpan;

  if (isOvernight) {
    displayDate = firstDate.toLocaleDateString('en-US', { timeZone: CHICAGO_TIMEZONE });
    timeSpan = `${firstDate.toLocaleTimeString('en-US', { timeZone: CHICAGO_TIMEZONE })} - ${lastDate.toLocaleTimeString('en-US', { timeZone: CHICAGO_TIMEZONE })} (+1 day)`;
  } else {
    displayDate = firstDate.toLocaleDateString('en-US', { timeZone: CHICAGO_TIMEZONE });
    timeSpan = `${firstDate.toLocaleTimeString('en-US', { timeZone: CHICAGO_TIMEZONE })} - ${lastDate.toLocaleTimeString('en-US', { timeZone: CHICAGO_TIMEZONE })}`;
  }

  return {
    isOvernight,
    displayDate,
    timeSpan,
    firstRecord: firstRecord.timestamp,
    lastRecord: lastRecord.timestamp
  };
};